/**
 * Client dashboard showing the user's submitted projects, progress bars,
 * downloadable deliverables, and deployed website links.
 *
 * Redirects unauthenticated visitors to the login page.
 *
 * @module DashboardPage
 */

import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid2 as Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  Skeleton,
  alpha,
  TextField,
  IconButton,
  Collapse,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import { motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import DrawIcon from '@mui/icons-material/Draw';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DoneIcon from '@mui/icons-material/Done';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import ProjectProgressBar from '../components/ProjectProgressBar';
import MarkdownPreview from '../components/MarkdownPreview';
import CelebrationDialog from '../components/CelebrationDialog';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FloatingOrb } from '../components/AnimatedComponents';

/** A single file deliverable attached to a project. */
interface Deliverable {
  platform: string;
  filename: string;
  blob_name: string;
  uploaded_at: string;
}

/** A message in the project conversation. */
interface Message {
  id: string;
  project_id: string;
  sender_id: string;
  sender_role: string;
  content: string;
  created_at: string;
  read_by_admin: boolean;
  read_by_client: boolean;
}

/** Quote data attached to a project. */
interface QuoteData {
  summary: string;
  contract_text: string;
  total_price: number | null;
  currency: string;
  reference_number: string | null;
  generated_at: string | null;
  admin_signature: boolean;
  admin_signed_at: string | null;
  client_signature: boolean;
  client_signed_at: string | null;
  pdf_blob_name: string | null;
}

/** Client-facing project data including status and deliverables. */
interface Project {
  id: string;
  project_type: string;
  app_name: string;
  description: string;
  target_audience?: string | null;
  key_features?: string[] | null;
  ai_features?: string[] | null;
  design_preferences?: string[] | null;
  logo_blob_name?: string | null;
  timeline?: string | null;
  deadline?: string | null;
  budget_range?: string | null;
  additional_notes?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  deliverables: Deliverable[];
  website_url?: string;
  website_url_public?: boolean;
  preview_token?: string;
  unread_client?: number;
  unread_admin?: number;
  quote?: QuoteData | null;
}

/** Dashboard page listing the user's projects with progress and downloads. */
export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [expandedMessages, setExpandedMessages] = useState<string | null>(null);
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [messageInput, setMessageInput] = useState<Record<string, string>>({});
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [signingProjectId, setSigningProjectId] = useState<string | null>(null);
  const signatureCanvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signingLoading, setSigningLoading] = useState(false);
  const [signWarningProjectId, setSignWarningProjectId] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    api
      .get('/api/projects/')
      .then((res) => setProjects(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const handleDownload = async (projectId: string, platform: string) => {
    try {
      const res = await api.get(`/api/projects/${projectId}/deliverables/${platform}/download`);
      window.open(res.data.download_url, '_blank');
    } catch {
      alert(t('dashboard.downloadNotAvailable'));
    }
  };

  const handlePreviewWebsite = async (projectId: string) => {
    try {
      const res = await api.get(`/api/projects/${projectId}/preview-url`);
      window.open(res.data.preview_url, '_blank');
    } catch {
      alert(t('dashboard.downloadNotAvailable'));
    }
  };

  const loadMessages = async (projectId: string) => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/api/projects/${projectId}/messages`);
      setMessages((prev) => ({ ...prev, [projectId]: res.data }));
      await api.post(`/api/projects/${projectId}/messages/mark-read`);
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, unread_client: 0 } : p))
      );
    } catch {
      // ignore
    } finally {
      setLoadingMessages(false);
    }
  };

  const toggleMessages = async (projectId: string) => {
    if (expandedMessages === projectId) {
      setExpandedMessages(null);
    } else {
      setExpandedMessages(projectId);
      await loadMessages(projectId);
    }
  };

  const sendMessage = async (projectId: string) => {
    const content = messageInput[projectId]?.trim();
    if (!content) return;
    setSendingMessage(true);
    try {
      await api.post(`/api/projects/${projectId}/messages`, { content });
      setMessageInput((prev) => ({ ...prev, [projectId]: '' }));
      await loadMessages(projectId);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {
      alert(t('dashboard.messageFailed'));
    } finally {
      setSendingMessage(false);
    }
  };

  const initSignatureCanvas = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#002a3a';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  };

  const handleSignatureMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    setIsDrawing(true);
    const rect = canvas.getBoundingClientRect();
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleSignatureMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = e.currentTarget;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handleSignatureMouseUp = () => {
    setIsDrawing(false);
  };

  const handleClientSign = async (projectId: string) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const signature = canvas.toDataURL('image/png');
    setSigningLoading(true);
    try {
      const res = await api.post(`/api/projects/${projectId}/quote/sign-client`, { signature });
      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, ...res.data } : p)));
      setSigningProjectId(null);
      setShowCelebration(true);
    } catch {
      alert(t('dashboard.signFailed'));
    } finally {
      setSigningLoading(false);
    }
  };

  const handleDownloadContract = async (projectId: string) => {
    try {
      const res = await api.get(`/api/projects/${projectId}/quote/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contract.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert(t('dashboard.contractDownloadFailed'));
    }
  };

  const platformLabels: Record<string, string> = {
    android: t('dashboard.platformAndroid'),
    ios: t('dashboard.platformIos'),
    windows: t('dashboard.platformWindows'),
    macos: t('dashboard.platformMacos'),
    linux: t('dashboard.platformLinux'),
  };

  if (!user) return null;

  return (
    <PageTransition>
      <SEO title={t('dashboard.seoTitle')} description={t('dashboard.seoDesc')} canonical="/dashboard" noindex />
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(GREEN, 0.02)} 0%, transparent 40%)`,
        }}
      >
        <FloatingOrb size={300} color={GREEN} top={-80} right={-100} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('dashboard.welcome', { name: user.full_name })}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                  {t('dashboard.subtitle')}
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/submit-project')}
                sx={{ borderRadius: 3, px: 3 }}
              >
                {t('dashboard.newProject')}
              </Button>
            </Box>
          </motion.div>

          {loading ? (
            <Grid container spacing={3}>
              {[1, 2].map((i) => (
                <Grid size={{ xs: 12 }} key={i}>
                  <Card sx={{ p: 3 }}>
                    <Skeleton variant="text" width="30%" height={40} />
                    <Skeleton variant="text" width="60%" sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" height={20} sx={{ borderRadius: 2, mb: 1 }} />
                    <Skeleton variant="text" width="40%" />
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : projects.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Card
                sx={{
                  p: 6,
                  textAlign: 'center',
                  border: `2px dashed ${alpha(DARK_BLUE, 0.12)}`,
                  bgcolor: alpha(DARK_BLUE, 0.01),
                }}
                elevation={0}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: alpha(GREEN, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <FolderOpenIcon sx={{ fontSize: 40, color: GREEN }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
                  {t('dashboard.emptyTitle')}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {t('dashboard.emptyDesc')}
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate('/submit-project')}
                  sx={{ borderRadius: 3, px: 4 }}
                >
                  {t('dashboard.emptyButton')}
                </Button>
              </Card>
            </motion.div>
          ) : (
            <Grid container spacing={3}>
              {projects.map((project, index) => (
                <Grid size={{ xs: 12 }} key={project.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <Card
                      sx={{
                        p: { xs: 2, md: 3 },
                        border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 30px ${alpha(DARK_BLUE, 0.08)}`,
                          borderColor: alpha(GREEN, 0.15),
                        },
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                          <Box>
                            <Typography variant="h4" sx={{ fontWeight: 600 }}>{project.app_name}</Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                              <Chip
                                label={project.project_type === 'both' ? t('submit.typeBoth') : project.project_type === 'app' ? t('submit.typeApp') : t('submit.typeWebsite')}
                                size="small"
                                color="primary"
                              />
                              <Chip
                                label={t(`status.${project.status.replace('_', '-')}`, project.status.replace('_', ' '))}
                                size="small"
                                color="secondary"
                              />
                            </Stack>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard.submitted', { date: new Date(project.created_at).toLocaleDateString() })}
                          </Typography>
                        </Box>

                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          {project.description}
                        </Typography>

                        {/* Project details link */}
                        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<InfoOutlinedIcon />}
                            onClick={() => navigate(`/projects/${project.id}`)}
                            sx={{ borderRadius: 3, textTransform: 'none' }}
                          >
                            {t('dashboard.viewDetails')}
                          </Button>
                          {['submitted', 'accepted', 'quote_sent'].includes(project.status) && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditIcon />}
                              color="secondary"
                              onClick={() => navigate(`/projects/${project.id}?edit=true`)}
                              sx={{ borderRadius: 3, textTransform: 'none' }}
                            >
                              {t('dashboard.editDetails')}
                            </Button>
                          )}
                        </Stack>

                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t('dashboard.projectProgress')}
                          </Typography>
                          <ProjectProgressBar status={project.status} />
                        </Box>

                        {/* Quote & Contract */}
                        {project.quote && project.quote.summary && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              {t('dashboard.quoteSummary')}
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: alpha(GREEN, 0.04), borderRadius: 2, border: `1px solid ${alpha(GREEN, 0.15)}`, mb: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Chip
                                  label={`${project.quote.currency} ${project.quote.total_price?.toLocaleString()}`}
                                  color="secondary"
                                  size="small"
                                  sx={{ fontWeight: 700 }}
                                />
                                <Stack direction="row" spacing={1}>
                                  <Chip
                                    label={project.quote.admin_signature ? t('dashboard.adminSigned') : t('dashboard.adminNotSigned')}
                                    color={project.quote.admin_signature ? 'success' : 'default'}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={project.quote.client_signature ? t('dashboard.clientSigned') : t('dashboard.clientNotSigned')}
                                    color={project.quote.client_signature ? 'success' : 'default'}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Stack>
                              </Stack>
                              <MarkdownPreview content={project.quote.summary} maxHeight={250} />
                            </Box>

                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                              {t('dashboard.contractText')}
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: alpha(DARK_BLUE, 0.02), borderRadius: 2, border: `1px solid ${alpha(DARK_BLUE, 0.08)}`, mb: 2 }}>
                              <MarkdownPreview content={project.quote.contract_text} maxHeight={300} />
                            </Box>

                            {/* Action buttons */}
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {project.quote.admin_signature && !project.quote.client_signature && (
                                <Button
                                  variant="contained"
                                  color="secondary"
                                  startIcon={<DrawIcon />}
                                  onClick={() => setSignWarningProjectId(project.id)}
                                  sx={{ borderRadius: 3 }}
                                >
                                  {t('dashboard.signContract')}
                                </Button>
                              )}
                              {project.quote.admin_signature && project.quote.client_signature && (
                                <Button
                                  variant="outlined"
                                  startIcon={<PictureAsPdfIcon />}
                                  onClick={() => handleDownloadContract(project.id)}
                                  sx={{ borderRadius: 3 }}
                                >
                                  {t('dashboard.downloadContract')}
                                </Button>
                              )}
                              {!project.quote.admin_signature && (
                                <Alert severity="info" sx={{ width: '100%' }}>
                                  {t('dashboard.quoteAwaitingAdmin')}
                                </Alert>
                              )}
                            </Stack>
                          </>
                        )}

                        {/* Website URL */}
                        {project.website_url && (
                          <Box sx={{ mb: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {project.website_url_public ? (
                                <Tooltip title={t('dashboard.urlPublicNote')}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<PublicIcon />}
                                    href={project.website_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="small"
                                    sx={{ borderRadius: 3 }}
                                  >
                                    {t('dashboard.visitWebsite')}
                                  </Button>
                                </Tooltip>
                              ) : (
                                <Tooltip title={t('dashboard.urlSecureNote')}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<LockIcon />}
                                    onClick={() => handlePreviewWebsite(project.id)}
                                    size="small"
                                    sx={{ borderRadius: 3 }}
                                  >
                                    {t('dashboard.previewWebsite')}
                                  </Button>
                                </Tooltip>
                              )}
                              <Chip
                                icon={project.website_url_public ? <PublicIcon /> : <LockIcon />}
                                label={project.website_url_public ? t('dashboard.publicUrl') : t('dashboard.secureUrl')}
                                size="small"
                                color={project.website_url_public ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </Stack>
                          </Box>
                        )}

                        {/* Deliverables */}
                        {project.deliverables.length > 0 && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              {t('dashboard.downloads')}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {project.deliverables.map((d) => (
                                <Button
                                  key={d.platform}
                                  variant="outlined"
                                  size="small"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => handleDownload(project.id, d.platform)}
                                  sx={{ borderRadius: 3 }}
                                >
                                  {platformLabels[d.platform] || d.platform}
                                </Button>
                              ))}
                            </Stack>
                          </>
                        )}

                        {/* Messages */}
                        <Divider sx={{ my: 2 }} />
                        <Box>
                          <Button
                            onClick={() => toggleMessages(project.id)}
                            startIcon={<ChatBubbleOutlineIcon />}
                            sx={{ borderRadius: 3, textTransform: 'none' }}
                          >
                            {t('dashboard.messages')}
                            {(project.unread_client || 0) > 0 && (
                              <Chip
                                label={`${project.unread_client} ${t('dashboard.newMessages')}`}
                                size="small"
                                color="error"
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Button>

                          <Collapse in={expandedMessages === project.id}>
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: alpha(DARK_BLUE, 0.02),
                                borderRadius: 2,
                                border: `1px solid ${alpha(DARK_BLUE, 0.08)}`,
                              }}
                            >
                              {loadingMessages ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                  <CircularProgress size={24} />
                                </Box>
                              ) : (
                                <>
                                  <Box
                                    sx={{
                                      maxHeight: 300,
                                      overflowY: 'auto',
                                      mb: 2,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1,
                                    }}
                                  >
                                    {(messages[project.id] || []).length === 0 ? (
                                      <Typography
                                        color="text.secondary"
                                        sx={{ textAlign: 'center', py: 3, fontSize: '0.9rem' }}
                                      >
                                        {t('dashboard.noMessages')}
                                      </Typography>
                                    ) : (
                                      (messages[project.id] || []).map((msg) => (
                                        <Box
                                          key={msg.id}
                                          sx={{
                                            alignSelf: msg.sender_role === 'admin' ? 'flex-start' : 'flex-end',
                                            maxWidth: '75%',
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: msg.sender_role === 'admin'
                                              ? alpha(DARK_BLUE, 0.06)
                                              : alpha(GREEN, 0.1),
                                            border: `1px solid ${msg.sender_role === 'admin'
                                              ? alpha(DARK_BLUE, 0.1)
                                              : alpha(GREEN, 0.15)}`,
                                          }}
                                        >
                                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {msg.content}
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5, gap: 0.5 }}>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                            >
                                              {msg.sender_role === 'admin' ? t('dashboard.messageSenderBooge') : user?.full_name}
                                              {' · '}
                                              {new Date(msg.created_at).toLocaleString()}
                                            </Typography>
                                            {msg.sender_role !== 'admin' && (
                                              <Tooltip title={msg.read_by_admin ? t('dashboard.messageRead') : t('dashboard.messageDelivered')}>
                                                {msg.read_by_admin ? (
                                                  <DoneAllIcon sx={{ fontSize: 14, color: GREEN }} />
                                                ) : (
                                                  <DoneIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                )}
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </Box>
                                      ))
                                    )}
                                    <div ref={messagesEndRef} />
                                  </Box>
                                  <Stack direction="row" spacing={1}>
                                    <TextField
                                      fullWidth
                                      size="small"
                                      placeholder={t('dashboard.messagePlaceholder')}
                                      value={messageInput[project.id] || ''}
                                      onChange={(e) =>
                                        setMessageInput((prev) => ({
                                          ...prev,
                                          [project.id]: e.target.value,
                                        }))
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          sendMessage(project.id);
                                        }
                                      }}
                                      multiline
                                      maxRows={3}
                                      sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                      }}
                                    />
                                    <IconButton
                                      color="primary"
                                      onClick={() => sendMessage(project.id)}
                                      disabled={sendingMessage || !messageInput[project.id]?.trim()}
                                      sx={{
                                        bgcolor: alpha(GREEN, 0.1),
                                        '&:hover': { bgcolor: alpha(GREEN, 0.2) },
                                      }}
                                    >
                                      {sendingMessage ? (
                                        <CircularProgress size={20} />
                                      ) : (
                                        <SendIcon />
                                      )}
                                    </IconButton>
                                  </Stack>
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* Warning dialog before signing */}
      <Dialog
        open={!!signWarningProjectId}
        onClose={() => setSignWarningProjectId(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          {t('dashboard.signWarningTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('dashboard.signWarningMessage')}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('dashboard.signWarningDetails')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignWarningProjectId(null)}>
            {t('dashboard.signWarningCancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<DrawIcon />}
            onClick={() => {
              const projectId = signWarningProjectId;
              setSignWarningProjectId(null);
              if (projectId) {
                setSigningProjectId(projectId);
                setTimeout(() => initSignatureCanvas(signatureCanvasRef.current), 100);
              }
            }}
          >
            {t('dashboard.signWarningProceed')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature canvas dialog */}
      <Dialog
        open={!!signingProjectId}
        onClose={() => setSigningProjectId(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('dashboard.signatureTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('dashboard.signatureDesc')}
          </Typography>
          <Box
            sx={{
              border: `2px solid ${alpha(DARK_BLUE, 0.2)}`,
              borderRadius: 2,
              overflow: 'hidden',
              mb: 1,
            }}
          >
            <canvas
              ref={signatureCanvasRef}
              width={460}
              height={180}
              onMouseDown={handleSignatureMouseDown}
              onMouseMove={handleSignatureMouseMove}
              onMouseUp={handleSignatureMouseUp}
              onMouseLeave={handleSignatureMouseUp}
              style={{ cursor: 'crosshair', display: 'block', width: '100%' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => initSignatureCanvas(signatureCanvasRef.current)}>
            {t('dashboard.clearSignature')}
          </Button>
          <Button onClick={() => setSigningProjectId(null)}>
            {t('dashboard.signWarningCancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => signingProjectId && handleClientSign(signingProjectId)}
            disabled={signingLoading}
            startIcon={signingLoading ? <CircularProgress size={16} color="inherit" /> : <DrawIcon />}
          >
            {t('dashboard.confirmSign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Celebration dialog after successful contract signing */}
      <CelebrationDialog
        open={showCelebration}
        onClose={() => setShowCelebration(false)}
        title={t('dashboard.celebrationTitle')}
        message={t('dashboard.celebrationMessage')}
        subtitle={t('dashboard.celebrationSubtitle')}
        buttonText={t('dashboard.celebrationButton')}
      />
    </PageTransition>
  );
}
